// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@eisinc/App/Store/Selectors/GeneralSelectors";
import AddOutline from "@eisinc/XDL/Icons/AddOutline";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
    // XNG-Changes
    showIcon?: boolean;
    iconSrc?: React.ComponentType | string;
    iconPosition?: string;
    iconSize?: number;
    iconColor?: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface ProductAddToListLinkStyles {
    link?: LinkPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    addToListText?: TypographyPresentationProps;
}

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    // XNG-Changes
    showIcon,
    iconPosition,
    iconSrc,
    iconSize,
    iconColor,
    // XNG-Changes
    isAuthenticated,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToListLink, extendedStyles));

    // XNG-Changes : don't show if !isAuthenticated
    if (!product.canAddToWishlist || !isAuthenticated) {
        return null;
    }

    const addToListLinkClickHandler = () => {
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Changes: Change structure + Add icon source, size, and color.
    const srcOfIcon = iconSrc || AddOutline;
    const sizeOfIcon = iconSize || 14;
    const colorOfIcon = iconColor || "primary";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link
                {...styles.link}
                onClick={addToListLinkClickHandler}
                disabled={productInfo.qtyOrdered <= 0}
                {...otherProps}
            >
                {showIcon && iconPosition !== "right" && (
                    <Icon
                        src={srcOfIcon}
                        size={sizeOfIcon}
                        color={colorOfIcon}
                        css={css`
                            &,
                            * {
                                fill: ${getColor(colorOfIcon)};
                            }
                        `}
                    ></Icon>
                )}
                {/* XNG-Changes : change default label to Shopping List */}
                <Typography {...styles.addToListText}>{labelOverride ?? translate("Shopping List")}</Typography>

                {showIcon && iconPosition === "right" && (
                    <Icon
                        src={srcOfIcon}
                        size={sizeOfIcon}
                        color={colorOfIcon}
                        css={css`
                            &,
                            * {
                                fill: ${colorOfIcon};
                            }
                        `}
                    ></Icon>
                )}
            </Link>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAddToListLink));
